import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, API_BASE } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Plus, Trash, FloppyDisk, DownloadSimple, FileXls, FilePdf, GoogleLogo, Paperclip } from "@phosphor-icons/react";
import SearchableSelect from "@/components/SearchableSelect";
import { UNITS } from "@/lib/units";

const UNIT_OPTS = UNITS.map((u) => ({ value: u.value, label: u.label }));
const GST_OPTS = [0, 5, 12, 18, 28].map((v) => ({ value: v, label: `${v}%` }));
const ORDERING_OPTS = ["Pending", "PO Raised", "Dispatched", "Received", "Closed"].map((v) => ({ value: v, label: v }));

const emptyProduct = () => ({
  description: "", make: "", qty: 1, uom: "nos",
  unit_purchase_price: 0, total_purchase_price: 0, purchase_gst_pct: 18, purchase_gst_amount: 0,
  vendor_name: "", vendor_payment_terms: "", ordering_status: "Pending", purchase_due_date: "",
  unit_sell_price: 0, total_sell_price: 0, sell_gst_pct: 18, sell_gst_amount: 0,
  end_customer_payment_terms: "", margin: 0, margin_pct: 0, sell_due_date: "",
});
const emptyService = () => ({
  description: "", man_days: 0, total_mandays: 0, qty: 0, uom: "day",
  gst_pct: 18, gst_amount: 0, due_date: "",
});

function compProduct(ln) {
  const qty = Number(ln.qty) || 0;
  const up = Number(ln.unit_purchase_price) || 0;
  const us = Number(ln.unit_sell_price) || 0;
  const totalPurchase = +(qty * up).toFixed(2);
  const totalSell = +(qty * us).toFixed(2);
  const pGst = +(totalPurchase * (Number(ln.purchase_gst_pct) || 0) / 100).toFixed(2);
  const sGst = +(totalSell * (Number(ln.sell_gst_pct) || 0) / 100).toFixed(2);
  const margin = +(totalSell - totalPurchase).toFixed(2);
  const marginPct = totalSell ? +((margin / totalSell) * 100).toFixed(2) : 0;
  return { ...ln, total_purchase_price: totalPurchase, total_sell_price: totalSell,
           purchase_gst_amount: pGst, sell_gst_amount: sGst, margin, margin_pct: marginPct };
}
function compService(ln) {
  const total = +((Number(ln.man_days) || 0) * (Number(ln.qty) || 0)).toFixed(2);
  const gstPct = ln.gst_pct ?? ln.sell_gst_pct ?? 18;
  const gst = +(total * (Number(gstPct) || 0) / 100).toFixed(2);
  return { ...ln, total_mandays: total, gst_pct: gstPct, gst_amount: gst };
}

async function downloadFile(url, filename) {
  const tok = localStorage.getItem("smipl_token");
  const res = await fetch(url, { headers: { Authorization: `Bearer ${tok}` } });
  if (!res.ok) { toast.error("Download failed"); return; }
  const blob = await res.blob();
  const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = filename; a.click();
  URL.revokeObjectURL(a.href);
}

async function uploadFile(file) {
  const tok = localStorage.getItem("smipl_token");
  const fd = new FormData(); fd.append("file", file);
  const res = await fetch(`${API_BASE}/files/upload`, { method: "POST", headers: { Authorization: `Bearer ${tok}` }, body: fd });
  if (!res.ok) throw new Error("upload failed");
  return await res.json();
}

export default function OrderForm() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const editing = !!id && id !== "new";
  const [customers, setCustomers] = useState([]);
  const [allOpps, setAllOpps] = useState([]);
  const [products, setProducts] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [form, setForm] = useState({
    customer_id: "", opportunity_id: "", customer_po_number: "", customer_po_date: "",
    product_lines: [emptyProduct()],
    service_lines: [],
    attachment_ids: [],
    notes: "",
    status: "draft",
  });
  const [order, setOrder] = useState(null); // when editing

  const refreshCatalog = useCallback(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/vendors").then((r) => setVendors(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/accounts").then((r) => setCustomers(r.data));
    api.get("/opportunities").then((r) => setAllOpps(r.data));
    refreshCatalog();
    const onVis = () => { if (document.visibilityState === "visible") refreshCatalog(); };
    window.addEventListener("focus", refreshCatalog);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      window.removeEventListener("focus", refreshCatalog);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [refreshCatalog]);

  useEffect(() => {
    if (!editing) return;
    api.get(`/orders/${id}`).then((r) => {
      const d = r.data;
      setOrder(d);
      setForm({
        customer_id: d.customer_id || "",
        opportunity_id: d.opportunity_id || "",
        customer_po_number: d.customer_po_number || "",
        customer_po_date: d.customer_po_date || "",
        product_lines: (d.product_lines || []).map(compProduct),
        service_lines: (d.service_lines || []).map(compService),
        attachment_ids: d.attachment_ids || [],
        notes: d.notes || "",
        status: d.status || "draft",
      });
    });
  }, [id, editing]);

  const custOpts = useMemo(() => customers.map((c) => ({
    value: c.id, label: c.company_name,
    sub: [c.gstin, (c.billing || {}).city].filter(Boolean).join(" · "),
  })), [customers]);

  const oppOpts = useMemo(() => allOpps
    .filter((o) => !form.customer_id || o.account_id === form.customer_id)
    .map((o) => ({ value: o.id, label: o.name, sub: o.stage })),
    [allOpps, form.customer_id]);

  const totals = useMemo(() => {
    let sell = 0, purchase = 0, margin = 0, services = 0;
    (form.product_lines || []).forEach((ln) => {
      sell += (Number(ln.total_sell_price) || 0) + (Number(ln.sell_gst_amount) || 0);
      purchase += (Number(ln.total_purchase_price) || 0) + (Number(ln.purchase_gst_amount) || 0);
      margin += Number(ln.margin) || 0;
    });
    (form.service_lines || []).forEach((ln) => {
      services += (Number(ln.total_mandays) || 0) + (Number(ln.gst_amount) || 0);
    });
    return { sell, purchase, margin, services, grand: sell + services, marginPct: sell ? (margin / sell) * 100 : 0 };
  }, [form.product_lines, form.service_lines]);

  const updProduct = (i, patch) => {
    const arr = [...form.product_lines];
    arr[i] = compProduct({ ...arr[i], ...patch });
    setForm({ ...form, product_lines: arr });
  };
  const updService = (i, patch) => {
    const arr = [...form.service_lines];
    arr[i] = compService({ ...arr[i], ...patch });
    setForm({ ...form, service_lines: arr });
  };

  const addProduct = () => setForm({ ...form, product_lines: [...form.product_lines, emptyProduct()] });
  const rmProduct = (i) => { const a = [...form.product_lines]; a.splice(i, 1); setForm({ ...form, product_lines: a }); };
  const addService = () => setForm({ ...form, service_lines: [...(form.service_lines || []), emptyService()] });
  const rmService = (i) => { const a = [...form.service_lines]; a.splice(i, 1); setForm({ ...form, service_lines: a }); };

  const save = async () => {
    if (!form.customer_id) return toast.error("Pick a customer");
    if (!form.customer_po_number?.trim()) return toast.error("Customer PO Number is required");
    try {
      if (editing) {
        await api.put(`/orders/${id}`, form);
        toast.success("Saved");
        api.get(`/orders/${id}`).then((r) => setOrder(r.data));
      } else {
        const r = await api.post("/orders", form);
        toast.success("Order created");
        nav(`/orders/${r.data.id}`);
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const submitForApproval = async () => {
    if (!editing) return toast.error("Save the order first");
    try { await api.post(`/orders/${id}/submit`); toast.success("Submitted for Sales Manager approval");
      api.get(`/orders/${id}`).then((r) => setOrder(r.data)); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const approve = async () => {
    try { const r = await api.post(`/orders/${id}/approve`, { comment: "" }); toast.success(`Moved to ${r.data.status}`);
      api.get(`/orders/${id}`).then((rr) => setOrder(rr.data)); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const reject = async () => {
    const reason = window.prompt("Rejection reason");
    if (!reason) return;
    try { await api.post(`/orders/${id}/reject`, { comment: reason }); toast.success("Rejected");
      api.get(`/orders/${id}`).then((rr) => setOrder(rr.data)); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const onAttach = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    try {
      const ids = [];
      for (const f of files) { const r = await uploadFile(f); ids.push(r.id); }
      setForm({ ...form, attachment_ids: [...(form.attachment_ids || []), ...ids] });
      toast.success(`${ids.length} file(s) attached`);
    } catch { toast.error("Upload failed"); }
    e.target.value = "";
  };

  const canApproveSales = order && order.status === "pending_sales" && ["sales_manager", "super_admin"].includes(user?.role);
  const canApproveFin = order && order.status === "pending_finance" && ["hr", "super_admin"].includes(user?.role);
  const canSubmit = order && ["draft", "rejected"].includes(order.status);
  const readonly = editing && !["draft", "rejected"].includes(order?.status || "draft");

  return (
    <div data-testid="order-form-page">
      <PageHeader
        title={editing ? `Order ${order?.order_no || ""}` : "New Order"}
        description="Fill customer, products and services. Totals and margin auto-calculate."
        action={
          <div className="flex items-center gap-2 flex-wrap">
            {editing && (
              <>
                <Button variant="outline" onClick={() => downloadFile(`${API_BASE}/orders/${id}/export/excel`, `${order?.order_no || "order"}.xlsx`)} data-testid="order-exp-xlsx">
                  <FileXls size={16} className="mr-1.5" />Excel
                </Button>
                <Button variant="outline" onClick={() => downloadFile(`${API_BASE}/orders/${id}/export/pdf`, `${order?.order_no || "order"}.pdf`)} data-testid="order-exp-pdf">
                  <FilePdf size={16} className="mr-1.5" />PDF
                </Button>
                <Button variant="outline" onClick={() => downloadFile(`${API_BASE}/orders/${id}/export/csv`, `${order?.order_no || "order"}.csv`)} data-testid="order-exp-csv">
                  <GoogleLogo size={16} className="mr-1.5" />Google Sheets
                </Button>
              </>
            )}
            {!readonly && (
              <Button className="btn-brand" onClick={save} data-testid="order-save">
                <FloppyDisk size={16} className="mr-1.5" />Save
              </Button>
            )}
            {canSubmit && !readonly && <Button className="btn-brand" onClick={submitForApproval} data-testid="order-submit">Submit for approval</Button>}
            {canApproveSales && <Button className="btn-brand" onClick={approve} data-testid="order-approve-sales">Approve (Sales)</Button>}
            {canApproveFin && <Button className="btn-brand" onClick={approve} data-testid="order-approve-fin">Approve (Finance)</Button>}
            {(canApproveSales || canApproveFin) && <Button variant="outline" className="text-red-600" onClick={reject} data-testid="order-reject">Reject</Button>}
          </div>
        }
      />

      {order && (
        <div className="mb-4 flex items-center gap-3 flex-wrap">
          <span className="text-xs uppercase tracking-wider opacity-60">Status</span>
          <span className="text-xs uppercase tracking-wider font-semibold px-2 py-1 rounded bg-slate-200 dark:bg-slate-700">{order.status}</span>
          {order.approvals?.length > 0 && (
            <details className="text-xs opacity-80 ml-auto">
              <summary className="cursor-pointer">Approval history ({order.approvals.length})</summary>
              <ul className="pl-4 mt-1">
                {order.approvals.map((a, i) => (
                  <li key={i}>{a.action} by {a.by_name} ({a.role}) — {a.at}{a.comment ? ` · ${a.comment}` : ""}</li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}

      <Card className="p-4 mb-4">
        <div className="grid sm:grid-cols-4 gap-4">
          <div>
            <label className="text-xs uppercase opacity-70 mb-1 block">Customer Name *</label>
            <SearchableSelect value={form.customer_id} onChange={(v) => setForm({ ...form, customer_id: v, opportunity_id: "" })}
              options={custOpts} placeholder="Select customer" testId="order-customer" disabled={readonly} />
          </div>
          <div>
            <label className="text-xs uppercase opacity-70 mb-1 block">Opportunity Name</label>
            <SearchableSelect value={form.opportunity_id} onChange={(v) => setForm({ ...form, opportunity_id: v })}
              options={oppOpts} placeholder={form.customer_id ? "Select opportunity" : "Pick customer first"}
              disabled={readonly || !form.customer_id} clearable testId="order-opportunity" />
          </div>
          <div>
            <label className="text-xs uppercase opacity-70 mb-1 block">Customer PO Number *</label>
            <Input value={form.customer_po_number} onChange={(e) => setForm({ ...form, customer_po_number: e.target.value })}
                   placeholder="e.g. PO/2026/0453" disabled={readonly} data-testid="order-po-number" />
          </div>
          <div>
            <label className="text-xs uppercase opacity-70 mb-1 block">Customer PO Date</label>
            <Input type="date" value={form.customer_po_date || ""} onChange={(e) => setForm({ ...form, customer_po_date: e.target.value })}
                   disabled={readonly} data-testid="order-po-date" />
          </div>
        </div>
      </Card>

      <Tabs defaultValue="products">
        <TabsList>
          <TabsTrigger value="products" data-testid="tab-products">Products ({form.product_lines.length})</TabsTrigger>
          <TabsTrigger value="services" data-testid="tab-services">Services ({(form.service_lines || []).length})</TabsTrigger>
        </TabsList>

        <TabsContent value="products" className="mt-3">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm opacity-70">Each product line includes Purchase and Sell details side by side.</div>
            {!readonly && <Button variant="outline" size="sm" onClick={addProduct} data-testid="add-product-line"><Plus size={14} className="mr-1" />Add product</Button>}
          </div>
          <div className="space-y-3">
            {form.product_lines.map((ln, i) => (
              <ProductLineCard key={i} i={i} ln={ln} upd={updProduct} rm={rmProduct} readonly={readonly}
                               products={products} vendors={vendors} onOpen={refreshCatalog} />
            ))}
            {form.product_lines.length === 0 && <div className="text-xs opacity-60 py-6 text-center">No products yet.</div>}
          </div>
        </TabsContent>

        <TabsContent value="services" className="mt-3">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm opacity-70">Total Cost = Man Days × Cost / Man Day (GST auto-computed).</div>
            {!readonly && <Button variant="outline" size="sm" onClick={addService} data-testid="add-service-line"><Plus size={14} className="mr-1" />Add service</Button>}
          </div>
          <div className="space-y-3">
            {(form.service_lines || []).map((ln, i) => (
              <ServiceLineCard key={i} i={i} ln={ln} upd={updService} rm={rmService} readonly={readonly}
                               products={products} onOpen={refreshCatalog} />
            ))}
            {(form.service_lines || []).length === 0 && <div className="text-xs opacity-60 py-6 text-center">No services yet.</div>}
          </div>
        </TabsContent>
      </Tabs>

      {/* Totals + Attachments + Notes */}
      <div className="grid lg:grid-cols-3 gap-4 mt-5">
        <Card className="p-4 lg:col-span-2">
          <h4 className="text-sm font-semibold mb-2 flex items-center gap-2"><Paperclip size={16} />Attachments</h4>
          <input type="file" multiple onChange={onAttach} disabled={readonly} data-testid="order-attach" className="text-sm" />
          {form.attachment_ids?.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {form.attachment_ids.map((fid) => (
                <a key={fid} href={`${API_BASE}/files/${fid}/download?token=${localStorage.getItem("smipl_token")}`}
                   target="_blank" rel="noreferrer"
                   className="text-xs underline text-[#fd7e14]">📎 {fid.slice(0, 8)}…</a>
              ))}
            </div>
          )}
          <h4 className="text-sm font-semibold mb-2 mt-4">Notes</h4>
          <Textarea value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} disabled={readonly} />
        </Card>

        <Card className="p-4">
          <h4 className="text-sm font-semibold mb-3">Totals</h4>
          <div className="space-y-1.5 text-sm">
            <div className="flex justify-between"><span className="opacity-70">Purchase (incl. GST)</span><span className="font-medium">₹ {totals.purchase.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</span></div>
            <div className="flex justify-between"><span className="opacity-70">Product Sell (incl. GST)</span><span className="font-medium">₹ {totals.sell.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</span></div>
            <div className="flex justify-between"><span className="opacity-70">Services (incl. GST)</span><span className="font-medium" data-testid="order-services-total">₹ {totals.services.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</span></div>
            <div className="flex justify-between"><span className="opacity-70">Order Value (incl. GST)</span><span className="font-semibold" data-testid="order-grand-total">₹ {totals.grand.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</span></div>
            <div className="flex justify-between border-t pt-2 mt-2"><span className="opacity-70">Margin</span><span className="font-bold text-green-600">₹ {totals.margin.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</span></div>
            <div className="flex justify-between"><span className="opacity-70">Margin %</span><span className="font-bold">{totals.marginPct.toFixed(2)}%</span></div>
          </div>
        </Card>
      </div>
    </div>
  );
}

function ProductLineCard({ i, ln, upd, rm, readonly, products = [], vendors = [], onOpen }) {
  const prodOpts = (products || []).filter((p) => (p.kind || "product") === "product")
    .map((p) => ({ value: p.id, label: p.name, sub: [p.hsn_sac, p.oem].filter(Boolean).join(" · ") }));
  const vendorOpts = (vendors || []).map((v) => ({ value: v.name, label: v.name, sub: v.company_name || "" }));
  const onPickProduct = (pid) => {
    const p = (products || []).find((x) => x.id === pid);
    if (!p) return;
    upd(i, {
      product_id: pid,
      description: p.name,
      make: p.oem || p.category || ln.make,
      unit_sell_price: p.selling_price || ln.unit_sell_price,
      unit_purchase_price: p.purchase_price || ln.unit_purchase_price,
      sell_gst_pct: p.tax_pct ?? ln.sell_gst_pct,
      purchase_gst_pct: p.tax_pct ?? ln.purchase_gst_pct,
      uom: p.primary_unit || ln.uom,
    });
  };
  return (
    <Card className="p-3">
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs uppercase tracking-wider opacity-60">Product line #{i + 1}</div>
        {!readonly && <Button size="icon" variant="ghost" onClick={() => rm(i)}><Trash size={14} className="text-red-600" /></Button>}
      </div>
      <div className="grid sm:grid-cols-6 gap-2">
        <div className="sm:col-span-2">
          <L label="Product"><SearchableSelect value={ln.product_id || ""} onChange={onPickProduct}
            options={prodOpts} placeholder="Select product" disabled={readonly} clearable onOpen={onOpen}
            testId={`prod-line-${i}-product`} /></L>
        </div>
        <div className="sm:col-span-2">
          <L label="Description"><Input value={ln.description} onChange={(e) => upd(i, { description: e.target.value })} disabled={readonly} /></L>
        </div>
        <L label="Make"><Input value={ln.make} onChange={(e) => upd(i, { make: e.target.value })} disabled={readonly} /></L>
        <L label="Qty"><Input type="number" value={ln.qty} onChange={(e) => upd(i, { qty: e.target.value })} disabled={readonly} /></L>
      </div>
      <div className="grid sm:grid-cols-6 gap-2 mt-2">
        <L label="UOM"><SearchableSelect value={ln.uom} onChange={(v) => upd(i, { uom: v })} options={UNIT_OPTS} disabled={readonly} /></L>
      </div>
      <div className="grid md:grid-cols-2 gap-3 mt-3">
        <div className="border border-slate-200 dark:border-slate-700 rounded-md p-3 bg-slate-50/50 dark:bg-slate-800/20">
          <div className="text-xs font-semibold uppercase tracking-wider mb-2 opacity-80">Purchase</div>
          <div className="grid grid-cols-2 gap-2">
            <L label="Unit Purchase Price"><Input type="number" value={ln.unit_purchase_price} onChange={(e) => upd(i, { unit_purchase_price: e.target.value })} disabled={readonly} /></L>
            <L label="Total Purchase Price"><Input readOnly value={ln.total_purchase_price} /></L>
            <L label="GST %"><SearchableSelect value={Number(ln.purchase_gst_pct)} onChange={(v) => upd(i, { purchase_gst_pct: v })} options={GST_OPTS} disabled={readonly} /></L>
            <L label="GST Amount"><Input readOnly value={ln.purchase_gst_amount} /></L>
            <L label="Vendor Name"><SearchableSelect value={ln.vendor_name || ""} onChange={(v) => upd(i, { vendor_name: v })}
              options={vendorOpts} placeholder="Pick vendor" disabled={readonly} clearable onOpen={onOpen} testId={`prod-line-${i}-vendor`} /></L>
            <L label="Vendor Payment Terms"><Input value={ln.vendor_payment_terms} onChange={(e) => upd(i, { vendor_payment_terms: e.target.value })} disabled={readonly} /></L>
            <L label="Ordering Status"><SearchableSelect value={ln.ordering_status} onChange={(v) => upd(i, { ordering_status: v })} options={ORDERING_OPTS} disabled={readonly} /></L>
            <L label="Due Date"><Input type="date" value={ln.purchase_due_date || ""} onChange={(e) => upd(i, { purchase_due_date: e.target.value })} disabled={readonly} /></L>
          </div>
        </div>
        <div className="border border-slate-200 dark:border-slate-700 rounded-md p-3 bg-orange-50/40 dark:bg-orange-950/10">
          <div className="text-xs font-semibold uppercase tracking-wider mb-2 opacity-80">Sell</div>
          <div className="grid grid-cols-2 gap-2">
            <L label="Unit Sell Price"><Input type="number" value={ln.unit_sell_price} onChange={(e) => upd(i, { unit_sell_price: e.target.value })} disabled={readonly} /></L>
            <L label="Total Sell Price"><Input readOnly value={ln.total_sell_price} /></L>
            <L label="GST %"><SearchableSelect value={Number(ln.sell_gst_pct)} onChange={(v) => upd(i, { sell_gst_pct: v })} options={GST_OPTS} disabled={readonly} /></L>
            <L label="GST Amount"><Input readOnly value={ln.sell_gst_amount} /></L>
            <L label="End Customer Payment Terms"><Input value={ln.end_customer_payment_terms} onChange={(e) => upd(i, { end_customer_payment_terms: e.target.value })} disabled={readonly} /></L>
            <L label="Margin"><Input readOnly value={ln.margin} /></L>
            <L label="Margin %"><Input readOnly value={ln.margin_pct} /></L>
            <L label="Due Date"><Input type="date" value={ln.sell_due_date || ""} onChange={(e) => upd(i, { sell_due_date: e.target.value })} disabled={readonly} /></L>
          </div>
        </div>
      </div>
    </Card>
  );
}

function ServiceLineCard({ i, ln, upd, rm, readonly, products = [], onOpen }) {
  const servOpts = (products || []).filter((p) => (p.kind || "product") === "service")
    .map((p) => ({ value: p.id, label: p.name, sub: p.hsn_sac || "" }));
  const onPickService = (pid) => {
    const p = (products || []).find((x) => x.id === pid);
    if (!p) return upd(i, { service_id: "" });
    upd(i, { service_id: pid, description: p.name, gst_pct: p.tax_pct ?? ln.gst_pct, uom: p.primary_unit || ln.uom });
  };
  return (
    <Card className="p-3" data-testid={`serv-line-${i}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs uppercase tracking-wider opacity-60">Service line #{i + 1}</div>
        {!readonly && <Button size="icon" variant="ghost" onClick={() => rm(i)} data-testid={`serv-line-${i}-remove`}><Trash size={14} className="text-red-600" /></Button>}
      </div>
      <div className="grid sm:grid-cols-6 gap-2">
        <div className="sm:col-span-2">
          <L label="Service"><SearchableSelect value={ln.service_id || ""} onChange={onPickService}
            options={servOpts} placeholder="Select service" disabled={readonly} clearable onOpen={onOpen}
            testId={`serv-line-${i}-service`} /></L>
        </div>
        <div className="sm:col-span-2">
          <L label="Description"><Input value={ln.description} onChange={(e) => upd(i, { description: e.target.value })} disabled={readonly} data-testid={`serv-line-${i}-desc`} /></L>
        </div>
        <L label="Man Days"><Input type="number" value={ln.man_days} onChange={(e) => upd(i, { man_days: e.target.value })} disabled={readonly} data-testid={`serv-line-${i}-mandays`} /></L>
        <L label="Cost / Man Days"><Input type="number" value={ln.qty} onChange={(e) => upd(i, { qty: e.target.value })} disabled={readonly} data-testid={`serv-line-${i}-rate`} /></L>
      </div>
      <div className="grid sm:grid-cols-6 gap-2 mt-2">
        <L label="Total Cost"><Input readOnly value={ln.total_mandays} data-testid={`serv-line-${i}-total`} /></L>
        <L label="UOM"><SearchableSelect value={ln.uom} onChange={(v) => upd(i, { uom: v })} options={UNIT_OPTS} disabled={readonly} /></L>
        <L label="GST %"><SearchableSelect value={Number(ln.gst_pct)} onChange={(v) => upd(i, { gst_pct: v })} options={GST_OPTS} disabled={readonly} testId={`serv-line-${i}-gst`} /></L>
        <L label="GST Amount"><Input readOnly value={ln.gst_amount} data-testid={`serv-line-${i}-gst-amt`} /></L>
        <L label="Due Date"><Input type="date" value={ln.due_date || ""} onChange={(e) => upd(i, { due_date: e.target.value })} disabled={readonly} /></L>
      </div>
    </Card>
  );
}

function L({ label, children }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider opacity-70 mb-0.5 block">{label}</label>
      {children}
    </div>
  );
}
